<div class="tprm-next-dashboard">
  <section class="dashboard-command-hero" aria-label="TPRM risk intelligence command center">
    <div class="hero-copy">
      <div class="hero-eyebrow">
        <mat-icon>auto_awesome</mat-icon>
        AI-enabled TPRM command center
      </div>
      <h1>Third-party risk, refreshed for intelligent action.</h1>
      <p>
        Monitor portfolio health, lifecycle exposure, and risk movement from a modern control room designed for faster vendor decisions.
      </p>
      <div class="hero-outlets" aria-label="Risk intelligence outlets">
        <span>Regulatory pulse</span>
        <span>Cyber posture</span>
        <span>Financial stability</span>
        <span>Contract SLAs</span>
      </div>
    </div>
    <div class="hero-metrics" aria-label="Dashboard headline KPIs">
      <div class="metric-tile primary">
        <span class="metric-label">Portfolio coverage</span>
        <strong>{{ getLifecycleTotal() }}</strong>
        <span class="metric-caption">active vendor universe</span>
      </div>
      <div class="metric-tile danger">
        <span class="metric-label">High-risk queue</span>
        <strong>{{ getRiskOverviewCount('high risk') }}</strong>
        <span class="metric-caption">vendors needing attention</span>
      </div>
      <div class="metric-tile warning">
        <span class="metric-label">Past-due actions</span>
        <strong>{{ toCount(dashboardData?.vendorLifecycleOverview?.dueDateCount) }}</strong>
        <span class="metric-caption">watchlist items</span>
      </div>
    </div>
  </section>


  <div class="grid grid-cols-5 gap-5 dashboard-grid dashboard-workbench">
  <div class="col-span-3">
    <div class="">
      <section class="mb-4">
        <div
          class="command-welcome-card flex flex-wrap gap-9 py-3 pr-3 pl-4 rounded-xl bg-neutral-100 border-neutral-100"
          role="banner"
          aria-label="Welcome banner with last login information"
        >
          <img
            loading="lazy"
            src="/images/dashboard/computer.png"
            class="object-contain shrink-0 aspect-square w-[65px]"
            alt="Welcome banner illustration"
          />
          <div
            class="flex flex-col grow shrink-0 my-auto basis-0 w-fit max-md:max-w-full"
          >
            <div class="flex items-center">
              <h1 class="flex-auto text-lg font-extrabold text-neutral-600">
                Hello! Welcome Again.
              </h1>

              <img
                loading="lazy"
                src="/images/dashboard/clock.svg"
                class="object-contain shrink-0 w-6 aspect-square size-5"
                alt=""
              />
              <span class="my-auto text-xs basis-auto">
                Last Login:
                <strong>{{
                  dashboardData?.lastLogin | date: "dd/MM/yyyy"
                }}</strong>
              </span>
            </div>
            <div
              class="mt-1 text-sm font-light text-neutral-600 max-md:max-w-full"
            >
              Track request status and monitor relevant third-party risk signals
              impacting your business.
            </div>
          </div>
        </div>
      </section>
      <section
        *ngIf="hasDetailedPerm('Dashboard', 'Vendor Lifecycle Overview', 'read')"
        class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel"
        role="region"
        aria-labelledby="overview-title"
      >
        <div class="section-header flex p-2">
          <h2
            id="overview-title"
            class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
          >
            Vendor Lifecycle Overview
            <mat-icon
              class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
              matTooltipPosition="above"
              matTooltip="Tracks total vendors by lifecycle status"
            >info_outline</mat-icon>
          </h2>
          <!-- <a class="ms-auto self-end text-sm text-black hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            role="link">
            View All
          </a> -->
        </div>
        <div class="p-5 bg-[#ececec] lifecycle-signal-board">
          <div class="panel-signal-caption"><span>Lifecycle intelligence signal</span><strong>Click any status count to drill into the vendor queue.</strong></div>
          <div class="flex gap-5 justify-between lifecycle-signal-row">
            <div
              class="flex flex-col px-3 py-2.5 text-white rounded-xl bg-slate-800"
              role="status"
              aria-label="All Vendors Count"
            >
              <div class="text-sm max-md:mr-1">All Vendors</div>
              <div
                class="text-3xl font-bold flex justify-center cursor-pointer"
                (click)="onVendorInventoryRedirect('')"
              >
                {{ getLifecycleTotal() }}
              </div>
            </div>
            <div class="lifecycle-status-grid grid grid-cols-5 divide-x flex-1 mt-3.5">
              <div
                class="flex flex-col items-center font-semibold text-[11px] text-[#000]"
              >
                In Progress
                <div
                  class="mt-2 text-3xl font-bold text-[#FFBD40] cursor-pointer"
                  (click)="onVendorInventoryRedirect('in-progress')"
                >
                  {{ getLifecycleStatusCount("in-progress") }}
                </div>
              </div>
              <div
                class="flex flex-col items-center text-[11px] font-semibold text-[#000]"
              >
                Active
                <div
                  class="mt-2 text-3xl font-bold text-[#00B25A] cursor-pointer"
                  (click)="onVendorInventoryRedirect('active')"
                >
                  {{ getLifecycleStatusCount("active") }}
                </div>
              </div>
              <div
                class="flex flex-col items-center text-[11px] font-semibold text-[#000]"
              >
                In Active
                <div
                  class="mt-2 text-3xl font-bold text-[#D61409] cursor-pointer"
                  (click)="onVendorInventoryRedirect('inactive')"
                >
                  {{ getLifecycleStatusCount("inactive") }}
                </div>
              </div>
              <div
                class="flex flex-col items-center text-[11px] font-semibold text-[#000]"
              >
                Offboarded
                <div
                  class="mt-2 text-3xl font-bold text-[#F86522] cursor-pointer"
                  (click)="onVendorInventoryRedirect('offboarded')"
                >
                  {{ getLifecycleStatusCount("offboarded") }}
                </div>
              </div>
              <div
                class="flex flex-col items-center text-[11px] text-nowrap font-semibold text-[#000]"
              >
                Past Due
                <div class="mt-2 text-3xl font-bold text-dark-500">
                  {{ toCount(dashboardData?.vendorLifecycleOverview?.dueDateCount) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section
        *ngIf="hasDetailedPerm('Dashboard', 'Business Risk Overview', 'read')"
        class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full mt-3 dashboard-panel"
        role="region"
        aria-labelledby="overview-title"
      >
        <div class="section-header flex p-2 items-center">
          <h2
            id="overview-title"
            class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
          >
            Business Risk Overview
            <mat-icon
              class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
              [matMenuTriggerFor]="riskMenu"
            >info_outline</mat-icon>
          </h2>
        </div>
        <mat-menu #riskMenu="matMenu" class="risk-popover">
            <div class="px-4 py-2" (click)="$event.stopPropagation()">
              <p class="text-sm text-gray-700 mb-2">
                Shows vendor risk distribution across tiers
              </p>
              <h4 class="font-bold text-[#800408] mb-2 border-b pb-1">
                Rating Range
              </h4>
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-[#c0392b] text-white">
                    <th class="border border-gray-300 p-2 text-left">
                      Risk Score
                    </th>
                    <th class="border border-gray-300 p-2 text-left">
                      Risk Score Band
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border border-gray-300 p-2">86 - 100</td>
                    <td class="border border-gray-300 p-2">Low Risk</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-300 p-2">73 - 85</td>
                    <td class="border border-gray-300 p-2">Moderate Risk</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-300 p-2">56 - 72</td>
                    <td class="border border-gray-300 p-2">Average Risk</td>
                  </tr>
                  <tr>
                    <td class="border border-gray-300 p-2">34 - 55</td>
                    <td class="border border-gray-300 p-2">
                      Below Average Risk
                    </td>
                  </tr>
                  <tr>
                    <td class="border border-gray-300 p-2">0 - 33</td>
                    <td class="border border-gray-300 p-2">High Risk</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </mat-menu>

          <mat-menu #riskMenu="matMenu" class="risk-table-menu">
  <div (click)="$event.stopPropagation()" class="p-2">
    <table class="risk-tooltip-table">
      <thead>
        <tr>
          <th>Score Range</th>
          <th>Indicator</th>
        </tr>
      </thead>
      <tbody>
        <tr class="row-low"><td>86-100</td><td>Low Risk</td></tr>
        <tr class="row-mod"><td>73-85</td><td>Moderate Risk</td></tr>
        <tr class="row-avg"><td>56-72</td><td>Average Risk</td></tr>
        <tr class="row-below"><td>34-55</td><td>Below Average</td></tr>
        <tr class="row-high"><td>Less than 33</td><td>High Risk</td></tr>
      </tbody>
    </table>
  </div>
</mat-menu>
        <div class="p-2 bg-[#ececec] risk-spectrum-shell">
          <div class="panel-signal-caption"><span>Risk spectrum signal</span><strong>High to low exposure distribution across scored third parties.</strong></div>
          <div class="flex items-center justify-between p-4 pb-0 risk-spectrum-inner">
            <div class="w-full risk-band-chart">
              <ul id="progressbar">
                <li class="critical cursor-pointer">
                  <span
                    class="count"
                    (click)="onBusinessRiskRedirect('high risk')"
                    >{{ getRiskOverviewCount("high risk") }}</span
                  >
                  <span class="arrow"></span>
                  <span class="value text-[#000]">High Risk</span>
                </li>
                <li class="caution cursor-pointer">
                  <span
                    class="count"
                    (click)="onBusinessRiskRedirect('below average risk')"
                    >{{ getRiskOverviewCount("below average risk") }}</span
                  >
                  <span class="arrow"></span>
                  <span class="value text-[#000]">Below Average Risk</span>
                </li>
                <li class="normal cursor-pointer">
                  <span
                    class="count"
                    (click)="onBusinessRiskRedirect('average risk')"
                    >{{ getRiskOverviewCount("average risk") }}</span
                  >
                  <span class="arrow"></span>
                  <span class="value text-[#000]">Average Risk</span>
                </li>
                <li class="stable cursor-pointer">
                  <span
                    class="count"
                    (click)="onBusinessRiskRedirect('moderate risk')"
                    >{{ getRiskOverviewCount("moderate risk") }}</span
                  >
                  <span class="arrow"></span>
                  <span class="value text-[#000]">Moderate Risk</span>
                </li>
                <li class="secure cursor-pointer">
                  <span
                    class="count"
                    (click)="onBusinessRiskRedirect('low risk')"
                    >{{ getRiskOverviewCount("low risk") }}</span
                  >
                  <span class="arrow"></span>
                  <span class="value text-[#000]">Low Risk</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
    <div class="">
      <section
        *ngIf="hasDetailedPerm('Dashboard', 'Open Activity Status', 'read')"
        class="relative w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-open-activity tprm-component-card open-activity-redesign"
        role="region"
        aria-labelledby="open-activity-title"
      >
        <div class="section-header nextgen-section-header flex justify-between p-2">
          <div class="section-title-group">
            <span class="section-eyebrow">Workflow Command Center</span>
            <h2
              id="open-activity-title"
              class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
            >
              Open Activity Status
              <mat-icon
                class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                matTooltipPosition="above"
                matTooltip="Count of Assessment and Risk Tiering at each stage"
              >info_outline</mat-icon>
            </h2>
            <p class="section-subtitle">Live queue posture across open assessments and risk-tiering workflows.</p>
          </div>
          <div class="section-actions">
            <mat-form-field
              class="custom-small-select"
              appearance="fill"
              floatLabel="always"
            >
              <mat-select
                placeholder="Select"
                class="type-select text-[#000]"
                [value]="openActivityStatusType"
                (selectionChange)="onOpenAssessementStatusChange($event)"
              >
                <mat-option se [value]="'assessment'">Assessment</mat-option>
                <mat-option [value]="'rtt'">Risk Tiering</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
        <div class="activity-status-shell p-2 bg-[#ececec] pb-5 chart-stage activity-chart-stage">
          <div class="chart-intel-strip">
            <div>
              <span>Stage telemetry</span>
              <strong>Click any bar to inspect the active workflow queue.</strong>
            </div>
            <div class="ai-signal-pill">AI-prioritized triage</div>
          </div>
          <div class="chart-card activity-chart-card">
            <div class="chart-card-rail"></div>
            <app-bar-chart
              (dataEvent)="onOpenActStatusBarClick($event, 'assessment')"
              *ngIf="openActivityStatusType === 'assessment'"
              [data]="getChartData(dashboardData?.assessmentStatus)"
              barColor="bg-blue-500"
              [barColors]="{
                'Not Started': '#D61409',
                'In Progress': '#FFBD40',
                'Under Evaluation': '#F86522',
                'Under Review': '#3B82F6',
                Completed: '#00B25A',
                Rejected: '#D61409',
              }"
            >
            </app-bar-chart>

            <app-bar-chart
              *ngIf="openActivityStatusType === 'rtt'"
              (dataEvent)="onOpenActStatusBarClick($event, 'risk-tiering')"
              [data]="getChartData(dashboardData?.riskTieringStatus)"
              [barColors]="{
                'Not Started': '#D61409',
                'In Progress': '#FFBD40',
                'Under Evaluation': '#F86522',
                'Under Review': '#3B82F6',
                Completed: '#00B25A',
                Rejected: '#D61409',
              }"
            >
            </app-bar-chart>
          </div>
          <div class="workflow-legend-grid" aria-label="Open activity legend">
            <span class="workflow-legend-chip legend-danger"><i></i>Not Started</span>
            <span class="workflow-legend-chip legend-amber"><i></i>In Progress</span>
            <span class="workflow-legend-chip legend-orange"><i></i>Evaluation</span>
            <span class="workflow-legend-chip legend-blue"><i></i>Review</span>
            <span class="workflow-legend-chip legend-green"><i></i>Completed</span>
          </div>
        </div>
      </section>
    </div>
    <div class="">
      <div class="flex risk-overview-pair">
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Inherent Risk Overview', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-inherent-risk tprm-component-card risk-overview-card inherent-risk-redesign"
          role="region"
          aria-labelledby="inherent-risk-title"
        >
          <div class="section-header nextgen-section-header flex justify-between p-2">
            <div class="section-title-group">
              <span class="section-eyebrow">Risk Exposure Lens</span>
              <h2
                id="inherent-risk-title"
                class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
              >
                Inherent Risk Overview
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Count of inherent risk by severity across Assessment and Risk Tiering"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Pre-control risk concentration before mitigants are applied.</p>
            </div>
            <div class="section-actions">
              <mat-form-field
                class="custom-small-select"
                appearance="fill"
                floatLabel="always"
              >
                <mat-select
                  placeholder="Select"
                  class="type-select text-[#000]"
                  [value]="inherentRiskOverviewType"
                  (selectionChange)="onInherentRiskOverviewChange($event)"
                >
                  <mat-option [value]="'assessment'">Assessment</mat-option>
                  <mat-option [value]="'rtt'">Risk Tiering</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="risk-donut-shell p-2 bg-[#ececec] chart-stage donut-stage">
            <div class="risk-donut-card inherent-donut-card">
              <app-donut-chart
                *ngIf="inherentRiskOverviewData"
                [data]="getChartData(inherentRiskOverviewData)"
                [colorMap]="{
                  High: '#D61409',
                  Medium: '#FFBD40',
                  Low: '#00B25A',
                }"
                (dataEvent)="onInherentOverviewChange($event)"
              ></app-donut-chart>
            </div>
            <div class="severity-pill-row">
              <span class="severity-pill severity-high"><i></i>High</span>
              <span class="severity-pill severity-medium"><i></i>Medium</span>
              <span class="severity-pill severity-low"><i></i>Low</span>
            </div>
          </div>
        </section>
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Residual Risk Overview', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full ms-2 dashboard-panel panel-residual-risk tprm-component-card risk-overview-card residual-risk-redesign"
          role="region"
          aria-labelledby="residual-risk-title"
        >
          <div class="section-header nextgen-section-header flex p-2">
            <div class="section-title-group">
              <span class="section-eyebrow">Control Effectiveness Lens</span>
              <h2
                id="residual-risk-title"
                class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
              >
                Residual Risk Overview
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Count of residual risk by severity in Assessment"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Post-control exposure remaining after assessment response actions.</p>
            </div>
          </div>
          <div class="risk-donut-shell p-2 bg-[#ececec] chart-stage donut-stage">
            <div class="risk-donut-card residual-donut-card">
              <app-donut-chart
                *ngIf="residualRiskOverviewData"
                [data]="getChartData(residualRiskOverviewData)"
                [colorMap]="{
                  High: '#D61409',
                  Medium: '#FFBD40',
                  Low: '#00B25A',
                }"
                (dataEvent)="onRiskRiskOverviewChange($event)"
              ></app-donut-chart>
            </div>
            <div class="severity-pill-row">
              <span class="severity-pill severity-high"><i></i>High</span>
              <span class="severity-pill severity-medium"><i></i>Medium</span>
              <span class="severity-pill severity-low"><i></i>Low</span>
            </div>
          </div>
        </section>
      </div>
      <div class="flex" *ngIf="false">
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Inherent Risk Overview', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel"
          role="region"
          aria-labelledby="overview-title"
        >
          <div class="section-header flex justify-between p-2">
            <h2
              id="overview-title"
              class="self-start text-base font-extrabold text-zinc-800"
            >
              Evidence Overview
            </h2>
            <mat-form-field
              class="custom-small-select"
              appearance="fill"
              floatLabel="always"
            >
              <mat-select
                placeholder="Select"
                class="type-select text-[#000]"
                [value]="evidenceOverviewType"
                (selectionChange)="onEvidenceOverviewChange($event)"
              >
                <mat-option [value]="'assessment'">Assessment</mat-option>
                <mat-option [value]="'rtt'">Risk Tiering</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="p-2 bg-[#ececec] h-[193px] chart-stage donut-stage">
            <app-donut-chart
              *ngIf="evidenceOverviewData"
              [data]="evidenceOverviewData"
              [colorMap]="{
                Expired: '#D61409',
                Active: '#00B25A',
              }"
              (dataEvent)="onEvidenceOverviewChange($event)"
            ></app-donut-chart>
          </div>
        </section>
        <section
          *ngIf="
            false &&
            hasDetailedPerm('Dashboard', 'Inherent Risk Overview', 'read')
          "
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full ms-2"
          role="region"
          aria-labelledby="overview-title"
        >
          <div class="section-header flex justify-between p-2">
            <h2
              id="overview-title"
              class="self-start text-base font-extrabold text-zinc-800"
            >
              Past Due Date Overview
            </h2>
            <mat-form-field
              class="custom-small-select"
              appearance="fill"
              floatLabel="always"
            >
              <mat-select
                placeholder="Select"
                class="type-select text-[#000]"
                [value]="pastDueDateOverviewType"
                (selectionChange)="onPastDueDateOverviewChange($event)"
              >
                <mat-option [value]="'assessment'">Assessment</mat-option>
                <mat-option [value]="'rtt'">Risk Tiering</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="p-2 bg-[#ececec] h-[193px] chart-stage donut-stage">
            <app-donut-chart
              *ngIf="pastDueDateOverviewData"
              [data]="pastDueDateOverviewData"
              [colorMap]="{
                High: '#D61409',
                Medium: '#FFBD40',
                Low: '#00B25A',
              }"
              (dataEvent)="onInherentOverviewChange($event)"
            ></app-donut-chart>
          </div>
        </section>
      </div>
      <div class="flex">
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Risk Status', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-risk-status tprm-component-card risk-status-redesign"
          role="region"
          aria-labelledby="risk-status-title"
        >
          <div class="section-header nextgen-section-header flex p-2 justify-between">
            <div class="section-title-group">
              <span class="section-eyebrow">Risk Queue Health</span>
              <h2
                id="risk-status-title"
                class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
              >
                Risk Summary Status
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Number of open and closed risk responses in Assessment and Risk Tiering"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Open versus closed risk-response inventory by workflow type.</p>
            </div>
            <div class="section-actions risk-status-actions">
              <div class="status-indicator-row" aria-label="Risk status indicator badges">
                <span class="status-indicator-badge status-open"><i></i>Open</span>
                <span class="status-indicator-badge status-closed"><i></i>Closed</span>
              </div>
              <mat-form-field
                class="custom-small-select"
                appearance="fill"
                floatLabel="always"
              >
                <mat-select
                  placeholder="Select"
                  class="type-select text-[#000]"
                  [value]="riskStatusType"
                  (selectionChange)="onriskStatusChange($event)"
                >
                  <mat-option [value]="'assessment'">Assessment</mat-option>
                  <mat-option [value]="'rtt'">Risk Tiering</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="risk-status-shell p-2 bg-[#ececec] chart-stage risk-status-stage">
            <div class="chart-card risk-status-chart-card">
              <div class="chart-card-rail"></div>
              <app-bar-chart
                (dataEvent)="onRiskStatusBarClick($event)"
                [data]="getChartData(riskStatus)"
                [dynamicWidth]="true"
                [barAlign]="'justify-center'"
                [barColors]="{
                  Open: '#D61409',
                  Closed: '#00B25A',
                }"
              ></app-bar-chart>
            </div>
            <div class="risk-status-metrics" *ngIf="getChartData(riskStatus)?.length">
              <div
                class="risk-status-meter"
                *ngFor="let status of getChartData(riskStatus)"
                [class.status-meter-open]="status.label === 'Open'"
                [class.status-meter-closed]="status.label === 'Closed'"
              >
                <span>{{ status.label || '--' }}</span>
                <strong>{{ status.count || 0 }}</strong>
              </div>
            </div>
          </div>
        </section>
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Risk Response Overview', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full ms-2 dashboard-panel panel-risk-response tprm-component-card risk-response-redesign"
          role="region"
          aria-labelledby="risk-result-title"
        >
          <div class="section-header nextgen-section-header flex p-2 justify-between">
            <div class="section-title-group">
              <span class="section-eyebrow">Response Decision Lens</span>
              <h2
                id="risk-result-title"
                class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
              >
                Risk Result Overview
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Distribution of closed risks by response: Reduced, Avoided, Ignored, Accepted, or Transferred"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Closed-risk response distribution across mitigation strategies.</p>
            </div>
            <div class="section-actions">
              <mat-form-field
              class="custom-small-select"
              appearance="fill"
              floatLabel="always"
            >
              <mat-select
                placeholder="Select"
                class="type-select text-[#000]"
                [value]="riskResponseOverviewType"
                (selectionChange)="onRiskResponseOverviewChange($event)"
              >
                <mat-option [value]="'assessment'">Assessment</mat-option>
                <mat-option [value]="'rtt'">Risk Tiering</mat-option>
              </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="risk-donut-shell p-2 bg-[#ececec] chart-stage donut-stage response-donut-stage">
            <div class="risk-donut-card response-donut-card">
              <app-donut-chart
              *ngIf="riskResultSummaryData"
              [data]="getChartData(riskResultSummaryData)"
              [colorMap]="{
                Reduced: '#00B25A',
                Avoided: '#7DD259',
                Ignored: '#F86522',
                Accepted: '#FFBD40',
                Transferred: '#3B82F6',
              }"
                (dataEvent)="onRiskResOverviewClick($event)"
              ></app-donut-chart>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>

  <div class="col-span-2">
    <div class="">
      <section
        *ngIf="hasDetailedPerm('Dashboard', 'Vendor Count by Category', 'read')"
        class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-vendor-count tprm-component-card vendor-count-redesign"
        role="region"
        aria-label="Risk Assessment Dashboard"
      >
        <div class="section-header nextgen-section-header flex p-2 justify-between">
          <div class="section-title-group">
            <span class="section-eyebrow">Vendor Coverage Lens</span>
            <h2
            id="overview-title"
            class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1"
          >
            Vendor Count
            <mat-icon
              class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
              matTooltipPosition="above"
              matTooltip="Vendors grouped by category and department"
              >info_outline</mat-icon>
            </h2>
            <p class="section-subtitle">Vendor population grouped by category or department ownership.</p>
          </div>
          <div class="section-actions">
            <mat-form-field
            class="custom-small-select"
            appearance="fill"
            floatLabel="always"
          >
            <mat-select
              placeholder="Select"
              class="type-select text-[#000]"
              [value]="vendorCountType"
              (selectionChange)="onVendorCountTypeChange($event)"
            >
              <mat-option [value]="'Category'">Category</mat-option>
              <mat-option [value]="'Department'">Department</mat-option>
            </mat-select>
            </mat-form-field>
          </div>
        </div>
        <div class="flex-col p-2 bg-[#ececec] chart-stage vendor-count-stage">
          <div class="chart-intel-strip">
            <div>
              <span>Population coverage</span>
              <strong>Switch category or department to inspect concentration.</strong>
            </div>
            <div class="ai-signal-pill">Vendor lens</div>
          </div>
          <div class="chart-card vendor-count-chart-card">
            <div class="chart-card-rail"></div>
                          <app-bar-chart
            (dataEvent)="onVendorCountByCatClick($event)"
            [data]="getChartData(dashboardData?.vendorCategoryCounts)"
            [dynamicWidth]="true"
            *ngIf="vendorCountType === 'Category'"
          ></app-bar-chart>
          <app-bar-chart
            (dataEvent)="onVendorCountByCatClick($event)"
            [data]="getChartData(dashboardData?.departmentCounts)"
            [dynamicWidth]="true"
              *ngIf="vendorCountType === 'Department'"
            ></app-bar-chart>

          </div>
        </div>
      </section>

      <div class="">
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Risk Summary', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-risk-summary tprm-component-card risk-summary-redesign"
          role="region"
          aria-label="Open Issue Summary"
        >
          <div class="section-header nextgen-section-header flex justify-between p-2">
            <div class="section-title-group">
              <span class="section-eyebrow">Open Risk Intelligence</span>
              <h2 class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1">
                Risk Summary
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Count of Open Risk Flags across the risk levels"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Severity distribution of active risk flags for analyst prioritisation.</p>
            </div>
            <div class="section-actions">
              <mat-form-field
                class="custom-small-select"
                appearance="fill"
                floatLabel="always"
              >
                <mat-select
                  placeholder="Select"
                  class="type-select text-[#000]"
                  [value]="riskSummaryType"
                  (selectionChange)="onOpenRiskSummaryChange($event)"
                >
                  <mat-option [value]="'assessment'">Assessment</mat-option>
                  <mat-option [value]="'rtt'">Risk Tiering</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="risk-summary-shell relative p-2 bg-[#ececec] min-h-[200px] chart-stage risk-summary-stage">
            <div class="risk-summary-severity-grid" aria-label="Risk summary severity legend">
              <span class="risk-summary-chip chip-critical"><i></i>Critical</span>
              <span class="risk-summary-chip chip-high"><i></i>High</span>
              <span class="risk-summary-chip chip-medium"><i></i>Medium</span>
              <span class="risk-summary-chip chip-low"><i></i>Low</span>
            </div>
            <div class="risk-summary-chart-card">
              <risk-summary-chart
                [class.hide-content]="isAllZero(riskSummary)"
                (dataEvent)="onRiskSummaryClick($event)"
                [chartData]="getChartData(riskSummary)"
              ></risk-summary-chart>
            </div>
            <ng-container *ngIf="isAllZero(riskSummary)">
              <ng-container
                *ngTemplateOutlet="
                  noDataTemplate;
                  context: { text: 'No active open risk flags in this view' }
                "
              ></ng-container>
            </ng-container>
          </div>
        </section>
      </div>

      <div class="">
        <section
          *ngIf="hasDetailedPerm('Dashboard', 'Recent Activities', 'read')"
          class="w-full bg-zinc-100 border-neutral-100 max-md:max-w-full dashboard-panel panel-recent-activities tprm-component-card recent-activities-redesign"
          role="region"
          aria-label="Open Issue Summary"
        >
          <div class="section-header nextgen-section-header p-2 flex justify-between">
            <div class="section-title-group">
              <span class="section-eyebrow">Activity Command Log</span>
              <h2 class="self-start text-base font-extrabold text-zinc-800 flex items-center gap-1">
                Recent Activities
                <mat-icon
                  class="!text-[18px] !h-[18px] !w-[18px] cursor-pointer text-gray-400"
                  matTooltipPosition="above"
                  matTooltip="Shows 10 most recent Assessment and Risk Tiering records with status"
                >info_outline</mat-icon>
              </h2>
              <p class="section-subtitle">Newest assessment and risk-tiering movements requiring operational awareness.</p>
            </div>
            <div class="section-actions">
              <mat-form-field
                class="custom-small-select"
                appearance="fill"
                floatLabel="always"
              >
                <mat-select
                  placeholder="Select"
                  class="type-select text-[#000]"
                  [value]="recentActivityType"
                  (selectionChange)="onRecentActivitiesChange($event)"
                >
                  <mat-option [value]="'assessment'">Assessment</mat-option>
                  <mat-option [value]="'rtt'">Risk Tiering</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="p-2 bg-[#ececec] relative min-h-[250px] recent-activity-stage">
            <div class="panel-signal-caption table-caption activity-command-caption">
              <span>Latest workflow signal</span>
              <strong>Recent assessment and risk-tiering movement for analyst review.</strong>
            </div>
            <div
              class="recent-activity-table rounded-b-lg overflow-x-auto"
              [class.hide-content]="!recentActivities?.length"
            >
              <table class="w-full text-sm text-gray-700 nextgen-activity-grid">
                <thead>
                  <tr class="activity-table-head-row border-b-2 border-gray-400">
                    <th class="py-3 px-2 font-bold text-left border-r border-gray-400 text-nowrap">Sr. No.</th>
                    <th class="py-3 px-2 font-bold border-r border-gray-400 text-center">ID</th>
                    <th class="py-3 px-2 text-nowrap font-bold border-r border-gray-400 text-center">Vendor Name</th>
                    <th class="py-3 px-2 text-nowrap font-bold text-left border-r border-gray-400">Initiation Date</th>
                    <th class="py-3 px-2 text-nowrap font-bold text-center border-r border-gray-400">Updated Date</th>
                    <th class="py-3 px-2 text-nowrap font-bold border-r border-gray-400 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    class="activity-row"
                    *ngFor="
                      let activity of recentActivities;
                      index as i;
                      last as isLast
                    "
                    [ngClass]="{ 'activity-row-divider': !isLast }"
                  >
                    <td class="activity-index-cell py-2 px-2 border-r border-gray-400 text-center">
                      <span>{{ i + 1 }}</span>
                    </td>
                    <td class="activity-id-cell py-2 px-2 border-r border-gray-400">
                      <span class="text-nowrap">
                        <a
                          (click)="redirectRecentActivity(activity)"
                          class="activity-id-link text-primary font-semibold text-nowrap cursor-pointer text-center"
                        >
                          {{ activity.assessmentId || activity.rttId || "--" }}
                        </a>
                      </span>
                    </td>
                    <td class="activity-vendor-cell py-2 px-2 border-r border-gray-400 text-nowrap">
                      {{ activity.vendorName || "--" }}
                    </td>

                    <td class="activity-date-cell py-2 px-2 border-r border-gray-400 text-center">
                      {{ activity.creationDate ? (activity.creationDate | date: "dd/MM/yyyy") : "--" }}
                    </td>
                    <td class="activity-date-cell py-2 px-2 border-r border-gray-400 text-center">
                      {{
                        activity.completionDate
                          ? (activity.completionDate | date: "dd/MM/yyyy")
                          : "--"
                      }}
                    </td>
                    <td class="activity-status-cell py-2 px-2 border-r border-gray-400 text-nowrap text-center">
                      <span class="activity-status-pill {{ getStatusClass(activity.status) }}">
                        {{ activity.status || "--" }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="activity-table-footer" *ngIf="recentActivities?.length">
              <span>Showing latest {{ recentActivities?.length || 0 }} workflow events</span>
              <a
                class="activity-view-all"
                href="{{ recentActivityType === 'assessment' ? '/assessment' : '/risk-tiering' }}"
              >View All</a>
            </div>
            <ng-container
              *ngIf="!recentActivities?.length; else activityTable"
            >
              <ng-container
                *ngTemplateOutlet="
                  noDataTemplate;
                  context: { text: 'No Recent Activities Found' }
                "
              ></ng-container>
            </ng-container>
            <ng-template #activityTable> </ng-template>
          </div>
        </section>
      </div>
    </div>
  </div>
</div>
</div>

<ng-template #noDataTemplate let-text="text">
  <div
    class="w-[80%] absolute shadow-lg rounded-lg p-3 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-10 bg-white bg-opacity-95"
  >
    <div class="text-center">
      <mat-icon class="text-gray-400 text-6xl mb-4">visibility_off</mat-icon>
      <h3 class="text-lg font-medium text-gray-900 mb-3 px-16">
        {{ text || "No Records Found" }}
      </h3>
    </div>
  </div>
</ng-template>
